import React, { useState } from "react";
import { Modal, Col, Form, Button } from "antd";

import FileUpload from "../../tools/common/FileUpload";
import { CanvasCalibrationDrawer } from "./CanvasCalibrationDrawer";
import {
    setSeatSpacing,
    setRowSpacing,
    setRowSeatSize,
} from "../../../redux/designerPropertiesSlice";
import { useDispatch } from "react-redux";

const CanvasConfigModalComponent = ({ visible, onClose, form, data, canvasRef, onChangeCanvas }) => {
    const { getFieldDecorator, getFieldValue, validateFields } = form;
    const [calibrationVisible, setCalibrationVisible] = useState(false);
    const [imgSrc, setImgSrc] = useState(null);
    const [selectedImageFile, setSelectedImageFile] = useState(null);

    const dispatch = useDispatch();

    const readFileAsDataUrl = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });

    const getSelectedFile = (fileValue) => {
        if (!fileValue) return null;

        if (fileValue instanceof File) {
            return fileValue;
        }

        if (fileValue.file instanceof File) {
            return fileValue.file;
        }

        if (
            Array.isArray(fileValue) &&
            fileValue[0] &&
            fileValue[0].file instanceof File
        ) {
            return fileValue[0].file;
        }

        return null;
    };

    const openCalibrationDrawer = () => {
        validateFields((error) => {
            if (error) return;

            const fileValue = getFieldValue("file");
            const selectedFile = getSelectedFile(fileValue);

            if (!selectedFile) return;

            const nextImageSrc = URL.createObjectURL(selectedFile);

            setSelectedImageFile(selectedFile);
            setImgSrc(nextImageSrc);
            setCalibrationVisible(true);
        });
    };

    const closeCalibrationDrawer = () => {
        setCalibrationVisible(false);
    };

    // Determina si el workarea del editor tiene una imagen de fondo cargada.
    const hasCurrentImage = () => {
        const workarea = canvasRef?.handler?.workarea;
        return Boolean(workarea && (workarea.src || workarea.file));
    };

    const handleRemoveImage = async () => {
        // Elimina la imagen de fondo del workarea y limpia el campo del formulario.
        await canvasRef.handler.workareaHandler.setImage(null);
        canvasRef.handler.workarea.set({ src: null, file: null });
        form.resetFields(["file"]);

        // Registrar el cambio en el historial y persistir el layout del mapa
        // para que la eliminación se conserve al salir y volver a entrar.
        canvasRef.handler.transactionHandler.save("properties");
        if (onChangeCanvas) {
            await onChangeCanvas();
        }

        setSelectedImageFile(null);
    };

    const handleNext = () => {
        console.log("Siguiente paso de calibración");
    };

    const handleCalibrationComplete = async ({ imgSrc, spacing: { seatSpacing, rowSpacing }, seatSize }) => {
        // Persistir la imagen como data URL (base64) en lugar del blob URL temporal.
        // El blob URL creado con URL.createObjectURL se invalida al salir/recargar,
        // por lo que no puede guardarse en el JSON del mapa. El data URL sí se
        // serializa en workarea.src y se restaura al volver a entrar al editor.
        let persistableSrc = imgSrc;
        try {
            if (selectedImageFile) {
                persistableSrc = await readFileAsDataUrl(selectedImageFile);
            }
        } catch (error) {
            // Si falla la conversión, usamos el src recibido como respaldo.
            persistableSrc = imgSrc;
        }

        await canvasRef.handler.workareaHandler.setImage(persistableSrc);
        dispatch(setSeatSpacing(seatSpacing));
        dispatch(setRowSpacing(rowSpacing));
        dispatch(setRowSeatSize(seatSize));

        // Registrar el cambio en el historial y persistir el layout del mapa.
        // Agregar la imagen de fondo no dispara los eventos del canvas
        // (object:added no está suscrito), por lo que hay que guardar de forma
        // explícita para que la imagen se conserve al salir y volver a entrar.
        canvasRef.handler.transactionHandler.save("properties");
        if (onChangeCanvas) {
            await onChangeCanvas();
        }

        // Liberar el blob URL temporal usado durante la calibración.
        if (imgSrc && imgSrc.startsWith("blob:")) {
            URL.revokeObjectURL(imgSrc);
        }
        setSelectedImageFile(null);

        onClose();
    };

    return (
        <>
            <Modal
                title="Canvas"
                visible={visible}
                onCancel={onClose}
                footer={null}
                width={500}
                centered
                bodyStyle={{
                    height: "50vh",
                    overflow: "auto",
                    padding: 24
                }}
            >
                <Col span={24}>
                    {getFieldDecorator("file", {
                        rules: [{ required: false }],
                        initialValue: data?.file,
                    })(<FileUpload accept="image/*" />)}
                </Col>

                <Col span={24} style={{ marginTop: "16px" }}>
                    <Button
                        type="primary"
                        block
                        onClick={openCalibrationDrawer}
                    >
                        Comenzar calibración
                    </Button>
                </Col>

                {hasCurrentImage() && (
                    <Col span={24} style={{ marginTop: "12px" }}>
                        <Button
                            type="danger"
                            block
                            icon="delete"
                            onClick={() => {
                                Modal.confirm({
                                    title: "¿Eliminar imagen actual?",
                                    content:
                                        "Se quitará la imagen de calibración del mapa.",
                                    okText: "Eliminar",
                                    cancelText: "Cancelar",
                                    okType: "danger",
                                    onOk: handleRemoveImage,
                                });
                            }}
                        >
                            Eliminar
                        </Button>
                    </Col>
                )}
            </Modal>

            <CanvasCalibrationDrawer
                visible={calibrationVisible}
                onClose={closeCalibrationDrawer}
                onNext={handleNext}
                imgSrc={imgSrc}
                onComplete={handleCalibrationComplete} />
        </>
    );
};

export const CanvasConfigModal = Form.create({
    onValuesChange(props, changedValues) {
        if (!("file" in changedValues)) return;

        const getSelectedFile = (fileValue) => {
            if (!fileValue) return null;

            if (fileValue instanceof File) {
                return fileValue;
            }

            if (fileValue.file instanceof File) {
                return fileValue.file;
            }

            if (
                Array.isArray(fileValue) &&
                fileValue[0] &&
                fileValue[0].file instanceof File
            ) {
                return fileValue[0].file;
            }

            return null;
        };

        const selectedFile = getSelectedFile(changedValues.file);

        if (!selectedFile) {
            props.canvasRef?.handler?.workareaHandler?.setImage(null);
            props.form?.resetFields?.(["file"]);
        }
    },
})(CanvasConfigModalComponent);